// Empire Apps Chassis · connectors/health/dpdp-purposes
// DPDP 2023 consent purposes specific to health connectors.
// Health connectors carry the PHI sub-category flag — highest sensitivity.
// Audit log entries from these purposes are immutable for 7 years per
// DPDP 2023 + India medical records retention norms.

export type HealthPurpose =
  | 'connector-healthkit-read'
  | 'connector-googlefit-read'
  | 'connector-abdm-phr-read'
  | 'connector-abdm-phr-write'
  | 'connector-cowin-cert'
  | 'connector-gmail-1mg'
  | 'connector-gmail-pharmeasy'
  | 'connector-gmail-apollo247'
  | 'connector-gmail-practo'
  | 'connector-fitbit-read'
  | 'connector-garmin-upload'
  | 'connector-oura-read'
  | 'connector-web-bluetooth'
  | 'connector-lab-report-upload'
  | 'connector-prescription-photo';

export const PURPOSE_LABEL: Readonly<Record<HealthPurpose, string>> = Object.freeze({
  'connector-healthkit-read': 'Apple HealthKit (sleep, heart, steps, workouts, body composition)',
  'connector-googlefit-read': 'Google Fit (sessions, sleep, heart, steps)',
  'connector-abdm-phr-read': 'ABDM PHR — read your health locker (FHIR R4)',
  'connector-abdm-phr-write': 'ABDM PHR — write back to your health locker',
  'connector-cowin-cert': 'CoWIN vaccination certificate (digital + QR)',
  'connector-gmail-1mg': 'Gmail — 1mg order history (medicine deliveries + consults)',
  'connector-gmail-pharmeasy': 'Gmail — PharmEasy order history',
  'connector-gmail-apollo247': 'Gmail — Apollo 24/7 (consults, labs, meds)',
  'connector-gmail-practo': 'Gmail — Practo appointment + doctor history',
  'connector-fitbit-read': 'Fitbit Web API (heart, sleep, steps, weight)',
  'connector-garmin-upload': 'Garmin Connect CSV export (no public API)',
  'connector-oura-read': 'Oura Ring v2 API (sleep, readiness, activity, HRV)',
  'connector-web-bluetooth': 'Web Bluetooth wearable (Mi Band, BoAt, BLE heart-rate monitor)',
  'connector-lab-report-upload': 'Lab report PDF upload (Thyrocare / Dr Lal / SRL / Apollo / Metropolis)',
  'connector-prescription-photo': 'Prescription photo OCR + dosage extraction',
});

export const RETENTION_DAYS: Readonly<Record<HealthPurpose, number>> = Object.freeze({
  'connector-healthkit-read': 730,
  'connector-googlefit-read': 730,
  'connector-abdm-phr-read': 2555, // 7 years per India medical records
  'connector-abdm-phr-write': 2555,
  'connector-cowin-cert': 2555,
  'connector-gmail-1mg': 730,
  'connector-gmail-pharmeasy': 730,
  'connector-gmail-apollo247': 730,
  'connector-gmail-practo': 730,
  'connector-fitbit-read': 365,
  'connector-garmin-upload': 365,
  'connector-oura-read': 365,
  'connector-web-bluetooth': 90,
  'connector-lab-report-upload': 2555,
  'connector-prescription-photo': 2555,
});

export const PHI_SUBCATEGORY: Readonly<Record<HealthPurpose, 'PHI-high' | 'PHI-medium' | 'PHI-low'>> = Object.freeze({
  'connector-healthkit-read': 'PHI-high',
  'connector-googlefit-read': 'PHI-medium',
  'connector-abdm-phr-read': 'PHI-high',
  'connector-abdm-phr-write': 'PHI-high',
  'connector-cowin-cert': 'PHI-high',
  'connector-gmail-1mg': 'PHI-high',
  'connector-gmail-pharmeasy': 'PHI-high',
  'connector-gmail-apollo247': 'PHI-high',
  'connector-gmail-practo': 'PHI-medium',
  'connector-fitbit-read': 'PHI-medium',
  'connector-garmin-upload': 'PHI-medium',
  'connector-oura-read': 'PHI-medium',
  'connector-web-bluetooth': 'PHI-low',
  'connector-lab-report-upload': 'PHI-high',
  'connector-prescription-photo': 'PHI-high',
});

export function logConnectorConsent(
  log: (action: string, meta: Record<string, string | number>) => void,
  purpose: HealthPurpose,
  scope: string,
): void {
  log('connector-consent-granted', {
    purpose,
    scope,
    phiSubcategory: PHI_SUBCATEGORY[purpose],
    retentionDays: RETENTION_DAYS[purpose],
    label: PURPOSE_LABEL[purpose],
  });
}

export function logConnectorWithdraw(
  log: (action: string, meta: Record<string, string | number>) => void,
  purpose: HealthPurpose,
): void {
  log('connector-consent-withdrawn', {
    purpose,
    phiSubcategory: PHI_SUBCATEGORY[purpose],
    label: PURPOSE_LABEL[purpose],
  });
}
